// client/src/services/api.js
import axios from 'axios';
import { getUserFromToken } from '../utils/auth';

// Create axios instance with proper configuration (as in your file)
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, 
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 429) {
      window.dispatchEvent(new CustomEvent('reactorhub:ratelimit', {
        detail: { retryAfter: error.response.data?.retryAfter || 900 }
      }));
    }
    return Promise.reject(error);
  }
);

// Utility function to get auth headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  if (token) {
    return {
      Authorization: `Bearer ${token}`
    };
  }
  return {};
};

// Content API (from your provided file)
export const contentAPI = {
  getTrending: async (type = 'movie', page = 1) => { //
    try {
      const response = await api.get('/content/trending', { params: { type, page } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  searchContent: async (query, type = 'movie') => { //
    try {
      const response = await api.get('/content/search', { params: { query, type } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getContentDetails: async (contentId) => { //
    try {
      const response = await api.get(`/content/${contentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getContentBySlug: async (slug) => {
    try {
      const response = await api.get(`/content/by-slug/${encodeURIComponent(slug)}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Enhanced Reaction API with category support, PAGINATION, and SEASON/EPISODE filter
export const reactionAPI = {
  getReactions: async (contentId, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.category) params.append('category', options.category);
      if (options.limit) params.append('limit', options.limit);
      if (options.sortBy) params.append('sortBy', options.sortBy);
      if (options.page) params.append('page', options.page);
      if (options.pageSize) params.append('pageSize', options.pageSize);
      if (options.season) params.append('season', options.season);
      if (options.episode) params.append('episode', options.episode);
      if (options.reactorType && options.reactorType !== 'all') params.append('reactorType', options.reactorType);

      const response = await api.get(`/reactions/${contentId}?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getPopularReactors: async (category = 'all', limit = 10) => { //
    try {
      const response = await api.get('/reactions/channels/popular', { 
        params: { category, limit } 
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getFeaturedChannels: async (category = 'all', limit = 12) => { //
    try {
      const response = await api.get('/reactions/featured/channels', { 
        params: { category, limit } 
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getChannelInfo: async (channelId) => { //
    try {
      const response = await api.get(`/reactions/channels/${channelId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getReactionsByCategory: async (category, movie = 'popular movies', limit = 20) => { //
    try {
      const response = await api.get(`/reactions/category/${category}`, {
        params: { movie, limit }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  testDirect: async (movie = 'Fight Club') => { //
    try {
      const response = await api.get('/reactions/test/direct', { params: { movie } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  testMovie: async (movieTitle, category = 'all') => { //
    try {
      const response = await api.get(`/reactions/test/${encodeURIComponent(movieTitle)}`, {
        params: { category }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getApiUsage: async () => { //
    try {
      const response = await api.get('/reactions/api/usage');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getReactionOfWeek: async (audience = 'general') => {
    try {
      const response = await api.get('/reactions/featured/reaction-of-week', { params: { audience } });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ========================================
// PEAK MOMENTS API - ADD TO EXISTING api.js
// ========================================

// Peak Moments API
export const peakMomentsAPI = {
  // Get all peak moments with filtering
  getPeakMoments: async (options = {}) => {
    try {
      const {
        category = 'all',
        momentType = 'all',
        featured = false,
        limit = 20,
        page = 1,
        sortBy = 'priority'
      } = options;


      const params = new URLSearchParams();
      if (category !== 'all') params.append('category', category);
      if (momentType !== 'all') params.append('momentType', momentType);
      if (featured) params.append('featured', 'true');
      params.append('limit', limit.toString());
      params.append('page', page.toString());
      params.append('sortBy', sortBy);

      const response = await api.get(`/peak-moments?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get featured peak moments for homepage
  getFeaturedPeakMoments: async (limit = 10) => {
    try {
      const response = await api.get('/peak-moments/featured', { params: { limit } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get peak moments by category
  getPeakMomentsByCategory: async (category, limit = 20) => {
    try {
      const response = await api.get(`/peak-moments/category/${category}`, { params: { limit } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get peak moments by type
  getPeakMomentsByType: async (momentType, limit = 20) => {
    try {
      const response = await api.get(`/peak-moments/type/${momentType}`, { params: { limit } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get peak moments by channel
  getPeakMomentsByChannel: async (channelId, limit = 20) => {
    try {
      const response = await api.get(`/peak-moments/channel/${channelId}`, { params: { limit } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get single peak moment and increment views
  getPeakMoment: async (id) => {
    try {
      const response = await api.get(`/peak-moments/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Like a peak moment
  likePeakMoment: async (id) => {
    try {
      const response = await api.post(`/peak-moments/${id}/like`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Submit a new peak moment
  submitPeakMoment: async (momentData) => {
    try {
      const response = await api.post('/peak-moments/submit', momentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get peak moments statistics
  getPeakMomentsStats: async () => {
    try {
      const response = await api.get('/peak-moments/stats/overview');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const peakMomentScenesAPI = {
  getScenes: async (options = {}) => {
    try {
      const response = await api.get('/peak-moment-scenes', { params: options });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createScene: async (sceneData) => {
    try {
      const response = await api.post('/peak-moment-scenes', sceneData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  addReaction: async (sceneId, reactionData) => {
    try {
      const response = await api.post(`/peak-moment-scenes/${sceneId}/reactions`, reactionData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Admin Peak Moments API
export const adminPeakMomentsAPI = {
  // Persist a drag-and-drop order. `ids` is the full list, top first.
  reorderPeakMoments: async (ids) => api.post('/admin/peak-moments/reorder', { ids }),

  // Get all peak moments for admin management
  getAdminPeakMoments: async (options = {}) => {
    try {
      const {
        status = 'all',
        category = 'all',
        momentType = 'all',
        limit = 50,
        page = 1,
        sortBy = 'createdAt'
      } = options;

      
      const params = new URLSearchParams();
      if (status !== 'all') params.append('status', status);
      if (category !== 'all') params.append('category', category);
      if (momentType !== 'all') params.append('momentType', momentType);
      params.append('limit', limit.toString());
      params.append('page', page.toString());
      params.append('sortBy', sortBy);

      const response = await api.get(`/admin/peak-moments?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new peak moment
  createPeakMoment: async (momentData) => {
    try {
      const response = await api.post('/admin/peak-moments', momentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update peak moment
  updatePeakMoment: async (id, updates) => {
    try {
      const response = await api.put(`/admin/peak-moments/${id}`, updates);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Approve peak moment
  approvePeakMoment: async (id) => {
    try {
      const response = await api.post(`/admin/peak-moments/${id}/approve`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reject peak moment
  rejectPeakMoment: async (id, reason = 'No reason provided') => {
    try {
      const response = await api.post(`/admin/peak-moments/${id}/reject`, { reason });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete peak moment
  deletePeakMoment: async (id) => {
    try {
      const response = await api.delete(`/admin/peak-moments/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Bulk operations
  bulkActionPeakMoments: async (action, momentIds, updates = {}) => {
    try {
      const response = await api.post('/admin/peak-moments/bulk-action', {
        action,
        momentIds,
        updates
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get admin statistics
  getAdminPeakMomentsStats: async () => {
    try {
      const response = await api.get('/admin/peak-moments/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Vote API (from your provided file)
export const voteAPI = {
  submitVote: async (contentId, contentType, voteType, title = null, thumbnail = null) => { //
    try {
      const response = await api.post('/votes', { contentId, contentType, voteType, title, thumbnail });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getVoteCount: async (contentId) => { //
    try {
      const response = await api.get(`/votes/${contentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Featured Content API
export const featuredAPI = {
  getCarousel: async (audience = 'general') => {
    try {
      const response = await api.get('/featured/carousel', { params: { audience } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getFeaturedChannels: async (audience = 'general') => {
    try {
      const response = await api.get('/featured/channels', { params: { audience } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSidebarRankings: async () => {
    try {
      const response = await api.get('/featured/sidebar-rankings');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  search: async (query, type = 'video') => {
    try {
      const response = await api.get('/featured/search', { params: { query, type } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getReactorCarousel: async () => {
    try {
      const response = await api.get('/featured/reactor-carousel');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Enhanced Admin API - Integrating NEW Video Ingestion Methods
export const adminAPI = {
  // ========================================
  // QUOTA MANAGEMENT (from your provided file)
  // ========================================
  getQuotaStatus: async () => { //
    try {
      const response = await api.get('/admin/quota/status');
      return response;
    } catch (error) {
      throw error;
    }
  },
  resetQuota: async () => { //
    try {
      const response = await api.post('/admin/quota/reset');
      return response;
    } catch (error) {
      throw error;
    }
  },

  // ========================================
  // PEAK MOMENT CANDIDATES (mined from YouTube comments)
  // ========================================
  getPeakCandidateStats: async () => api.get('/admin/peak-candidates/stats'),
  // Costs 1 YouTube quota unit per video. Returns as soon as the job starts —
  // poll getPeakScanStatus() for progress, a batch runs for minutes.
  scanPeakCandidates: async ({ limit = 25, minLikes = 5, channelId = null } = {}) =>
    api.post('/admin/peak-candidates/scan', { limit, minLikes, channelId }),
  getPeakScanStatus: async () => api.get('/admin/peak-candidates/scan/status'),
  getPeakCandidates: async (status = 'pending', limit = 40) =>
    api.get('/admin/peak-candidates', { params: { status, limit } }),
  approvePeakCandidate: async (id, payload = {}) =>
    api.post(`/admin/peak-candidates/${id}/approve`, payload),
  rejectPeakCandidate: async (id) =>
    api.post(`/admin/peak-candidates/${id}/reject`),


  // ========================================
  // ENHANCED CHANNEL MANAGEMENT (from your provided file)
  // ========================================
  getAllChannelData: async () => { //
    try {
      const response = await api.get('/admin/channels/all');
      return response;
    } catch (error) {
      throw error;
    }
  },
  addChannelById: async (channelId, options = {}) => { //
    try {
      const requestData = { 
        channelId: channelId.trim(),
        customName: options.customName?.trim() || null,
        categories: options.categories || ['general'],
        primaryCategory: options.primaryCategory || 'general',
        listType: options.listType || 'whitelist',
        autoCarouselEligible: !!options.autoCarouselEligible
      };
      const response = await api.post('/admin/channels/add', requestData);
      return response;
    } catch (error) {
      throw error;
    }
  },
  addChannelId: async (channelName, channelId, options = {}) => { //
    try {
      const requestData = {
        channelName: channelName.trim(),
        channelId: channelId.trim(),
        categories: options.categories || ['general'],
        primaryCategory: options.primaryCategory || 'general'
      };
      const response = await api.post('/admin/channels/add-id', requestData);
      return response;
    } catch (error) {
      throw error;
    }
  },
  updateChannelCategories: async (channelName, categories, primaryCategory, reactorType = null, autoCarouselEligible = undefined) => { //
    try {
      const payload = {
        categories,
        primaryCategory
      };
      if (reactorType) {
        payload.reactorType = reactorType;
      }
      if (typeof autoCarouselEligible === 'boolean') {
        payload.autoCarouselEligible = autoCarouselEligible;
      }
      const response = await api.put(`/admin/channels/${encodeURIComponent(channelName)}`, payload);
      return response;
    } catch (error) {
      throw error;
    }
  },
  getChannelsByCategory: async (category) => { //
    try {
      const response = await api.get(`/admin/channels/category/${category}`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  refreshChannel: async (channelId) => { //
    try {
      const response = await api.put(`/admin/channels/${channelId}/refresh`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  deleteChannel: async (channelId) => { //
    try {
      const response = await api.delete(`/admin/channels/${channelId}`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  getChannelInfo: async (channelId) => { //
    try {
      const response = await api.get(`/admin/channels/info/${channelId}`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  validateChannelId: async (channelId) => { //
    try {
      const response = await api.post('/admin/channels/validate', { channelId });
      return response;
    } catch (error) {
      throw error;
    }
  },
  testDatabaseStorage: async () => { //
    try {
      const response = await api.post('/admin/channels/test-storage');
      return response;
    } catch (error) {
      throw error;
    }
  },
  getStoredChannels: async (page = 1, limit = 50, search = '') => { //
    try {
      const response = await api.get('/admin/channels/stored', { 
        params: { page, limit, search } 
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  // ========================================
  // WHITELIST MANAGEMENT (from your provided file)
  // ========================================
  getWhitelist: async () => { //
    try {
      const response = await api.get('/admin/whitelist');
      return response;
    } catch (error) {
      throw error;
    }
  },
  addToWhitelist: async (channelName) => { //
    try {
      const response = await api.post('/admin/whitelist/channels', { channelName });
      return response;
    } catch (error) {
      throw error;
    }
  },
  removeFromWhitelist: async (channelName) => { //
    try {
      const response = await api.delete(`/admin/whitelist/channels/${encodeURIComponent(channelName)}`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  // ========================================
  // BLACKLIST MANAGEMENT (from your provided file)
  // ========================================
  getBlacklist: async () => { //
    try {
      const response = await api.get('/admin/blacklist');
      return response;
    } catch (error) {
      throw error;
    }
  },
  addToBlacklist: async (channelName) => { //
    try {
      const response = await api.post('/admin/blacklist/channels', { channelName });
      return response;
    } catch (error) {
      throw error;
    }
  },
  removeFromBlacklist: async (channelName) => { //
    try {
      const response = await api.delete(`/admin/blacklist/channels/${encodeURIComponent(channelName)}`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  updateMinimumSubscribers: async (minimumSubscribers) => { //
    try {
      const response = await api.put('/admin/blacklist/minimum-subscribers', { minimumSubscribers });
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  // ========================================
  // ANALYTICS & REPORTING (from your provided file)
  // ========================================
  getAnalytics: async () => { //
    try {
      const response = await api.get('/admin/analytics');
      return response;
    } catch (error) {
      throw error;
    }
  },

  getDashboardOverview: async () => {
    const response = await api.get('/admin/analytics/overview');
    return response;
  },

  // ========================================
  // TESTING & VALIDATION (from your provided file)
  // ========================================
  testYouTube: async () => { //
    try {
      const response = await api.get('/admin/test-youtube');
      return response;
    } catch (error) {
      throw error;
    }
  },
  testAdmin: async () => { //
    try {
      const response = await api.get('/admin/test');
      return response;
    } catch (error) {
      throw error;
    }
  },
  healthCheck: async () => { //
    try {
      const response = await api.get('/admin/health');
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  // ========================================
  // FEATURED CONTENT (from your provided file)
  // ========================================
  getFeaturedReactions: async () => { //
    try {
      const response = await api.get('/admin/whitelisted-reactions');
      return response;
    } catch (error) {
      throw error;
    }
  },
  getFeaturedChannels: async (category = 'all', limit = 12) => { //
    try {
      const response = await api.get('/admin/whitelisted-channels', {
        params: { category, limit }
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  // ========================================
  // CATEGORY MANAGEMENT (from your provided file)
  // ========================================
  getCategoryStats: async () => { //
    try {
      const response = await api.get('/admin/categories/stats');
      return response;
    } catch (error) {
      throw error;
    }
  },
  updateChannelCategory: async (channelId, categories, primaryCategory) => { //
    try {
      const response = await api.put(`/admin/channels/${channelId}/categories`, {
        categories,
        primaryCategory
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  // ========================================
  // BULK OPERATIONS (from your provided file)
  // ========================================
  bulkAddChannels: async (channels) => { //
    try {
      const results = [];
      for (const channel of channels) {
        try {
          // Assuming adminAPI is accessible here or use this.addChannelById if it was part of a class
          const result = await adminAPI.addChannelById(channel.id, { // - recursive call to adminAPI
            customName: channel.name,
            categories: channel.categories || ['general'],
            primaryCategory: channel.primaryCategory || 'general'
          });
          results.push({ ...channel, success: true, result: result.data });
        } catch (error) {
          results.push({ ...channel, success: false, error: error.message });
        }
      }
      return results;
    } catch (error) {
      throw error;
    }
  },
  bulkUpdateCategories: async (updates) => { //
    try {
      const results = [];
      for (const update of updates) {
        try {
          // Assuming adminAPI is accessible here or use this.updateChannelCategories
          const result = await adminAPI.updateChannelCategories( // - recursive call to adminAPI
            update.channelName,
            update.categories,
            update.primaryCategory
          );
          results.push({ ...update, success: true, result: result.data });
        } catch (error) {
          results.push({ ...update, success: false, error: error.message });
        }
      }
      return results;
    } catch (error) {
      throw error;
    }
  },
  
  // ========================================
  // UTILITY FUNCTIONS (from your provided file)
  // ========================================
  adminHealthCheck: async () => { //
    try {
      const [quota, analytics, whitelist, channelsData] = await Promise.all([ // Renamed channels to channelsData to avoid conflict
        adminAPI.getQuotaStatus().catch(e => ({ error: e.message })),
        adminAPI.getAnalytics().catch(e => ({ error: e.message })),
        adminAPI.getWhitelist().catch(e => ({ error: e.message })),
        adminAPI.getAllChannelData().catch(e => ({ error: e.message }))
      ]);
      return {
        quota: quota.error ? { status: 'error', message: quota.error } : { status: 'ok', data: quota.data },
        analytics: analytics.error ? { status: 'error', message: analytics.error } : { status: 'ok', data: analytics.data },
        whitelist: whitelist.error ? { status: 'error', message: whitelist.error } : { status: 'ok', data: whitelist.data },
        channels: channelsData.error ? { status: 'error', message: channelsData.error } : { status: 'ok', data: channelsData.data }, // Used channelsData
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      // ... (rest of your error handling for adminHealthCheck)
      return {
        quota: { status: 'error', message: 'Health check failed (overall)' },
        analytics: { status: 'error', message: 'Health check failed (overall)' },
        whitelist: { status: 'error', message: 'Health check failed (overall)' },
        channels: { status: 'error', message: 'Health check failed (overall)' },
        timestamp: new Date().toISOString()
      };
    }
  },
  exportChannelData: async () => { //
    try {
      const response = await api.get('/admin/export/channels');
      return response;
    } catch (error) {
      throw error;
    }
  },
  importChannelData: async (data) => { //
    try {
      const response = await api.post('/admin/import/channels', data);
      return response;
    } catch (error) {
      throw error;
    }
  },

  // === NEW VIDEO INGESTION MANAGEMENT METHODS ===
  // Added here, within the adminAPI object
  getIngestionStatus: async () => {
    try {
      const response = await api.get('/admin/videos/ingestion-status');
      return response; 
    } catch (error) {
      throw error; 
    }
  },

  ingestAllVideosForChannel: async (youtubeChannelId) => {
    try {
      const response = await api.post(`/admin/channels/${youtubeChannelId}/ingest-all-videos`);
      return response; 
    } catch (error) {
      throw error;
    }
  },

  fetchNewVideosForChannel: async (youtubeChannelId) => {
    try {
      const response = await api.post(`/admin/channels/${youtubeChannelId}/fetch-new-videos`);
      return response; 
    } catch (error) {
      throw error;
    }
  },

  bulkManageVideos: async (channelIds, mode) => { // mode: 'all' or 'new'
    try {
      const response = await api.post('/admin/channels/bulk-manage-videos', { channelIds, mode });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // === NEW DIRECT DATABASE SEARCH METHODS ===
  searchVideosDirectly: async (searchParams) => {
    try {
      const response = await api.post('/admin/search/videos', searchParams);
      return response;
    } catch (error) {
      throw error;
    }
  },

  searchChannelsDirectly: async (searchParams) => {
    try {
      const response = await api.post('/admin/search/channels', searchParams);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getBoosts: async (status) => {
    const response = await api.get('/admin/boosts', { params: status ? { status } : {} });
    return response.data;
  },
  getCarouselAutomationSettings: async () => {
    const response = await api.get('/admin/carousel-automation/settings');
    return response;
  },
  updateCarouselAutomationSettings: async (settings) => {
    const response = await api.put('/admin/carousel-automation/settings', settings);
    return response;
  },
  refreshAutoCarousel: async (audience = 'general') => {
    const response = await api.post('/admin/carousel-automation/refresh', { audience });
    return response;
  },
  activateBoost: async (id, months = 1, adminNotes = '') => {
    const response = await api.post(`/admin/boosts/${id}/activate`, { months, adminNotes });
    return response.data;
  },
  cancelBoost: async (id, adminNotes = '') => {
    const response = await api.post(`/admin/boosts/${id}/cancel`, { adminNotes });
    return response.data;
  },
  approveBoostVideo: async (boostId, videoId, coolOffHours = 24) => {
    const response = await api.post(`/admin/boosts/${boostId}/videos/${videoId}/approve`, { coolOffHours });
    return response.data;
  },
  rejectBoostVideo: async (boostId, videoId, reason = '') => {
    const response = await api.post(`/admin/boosts/${boostId}/videos/${videoId}/reject`, { reason });
    return response.data;
  },
  setCarouselCoolOff: async (featuredId, hours) => {
    const response = await api.post(`/admin/featured/carousel/${featuredId}/cooloff`, { hours });
    return response.data;
  },
  getFeaturedSuggestions: async () => {
    try {
      const response = await api.get('/admin/featured/suggestions');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Sidebar Rankings Management
  getSidebarRankings: async () => {
    try {
      const response = await api.get('/admin/sidebar-rankings');
      return response;
    } catch (error) {
      throw error;
    }
  },

  updateSidebarRanking: async (position, data) => {
    try {
      const response = await api.put(`/admin/sidebar-rankings/${position}`, data);
      return response;
    } catch (error) {
      throw error;
    }
  },

  updateMostWantedRanking: async (index, data) => {
    try {
      const response = await api.put(`/admin/sidebar-rankings/most-wanted/${index}`, data);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getMediaLibrary: async (params = {}) => {
    try {
      const response = await api.get('/content/trending', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Featured Carousel Management
  getFeaturedCarousel: async (audience = 'general') => {
    try {
      const response = await api.get('/admin/featured-carousel', { params: { audience } });
      return response;
    } catch (error) {
      throw error;
    }
  },

  addFeaturedCarouselItem: async (data) => {
    try {
      const response = await api.post('/admin/featured-carousel', data);
      return response;
    } catch (error) {
      throw error;
    }
  },

  updateFeaturedCarouselItem: async (id, updates) => {
    try {
      const response = await api.put(`/admin/featured-carousel/${id}`, updates);
      return response;
    } catch (error) {
      throw error;
    }
  },

  deleteFeaturedCarouselItem: async (id) => {
    try {
      const response = await api.delete(`/admin/featured-carousel/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  reorderFeaturedCarousel: async (items) => {
    try {
      const response = await api.put('/admin/featured-carousel/reorder', { items });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Reaction of the Week Management
  getReactionOfWeek: async (audience = 'general') => {
    try {
      const response = await api.get('/admin/reaction-of-week', { params: { audience } });
      return response;
    } catch (error) {
      throw error;
    }
  },

  updateReactionOfWeek: async (slot, videoUrl, audience = 'general') => {
    try {
      const response = await api.post(`/admin/reaction-of-week/${slot}`, { videoUrl, audience });
      return response;
    } catch (error) {
      throw error;
    }
  }
  // === END OF NEW METHODS ===

}; // End of adminAPI object

// Admin Featured Content API
export const adminFeaturedAPI = {
  getAllFeatured: async (audience = 'general') => {
    try {
      const response = await api.get('/admin/featured/all', { params: { audience } });
      return response;
    } catch (error) {
      throw error;
    }
  },

  addCarouselItem: async (item) => {
    try {
      const response = await api.post('/admin/featured/carousel', item);
      return response;
    } catch (error) {
      throw error;
    }
  },

  addFeaturedChannel: async (channelData) => {
    try {
      const response = await api.post('/admin/featured/channel', channelData);
      return response;
    } catch (error) {
      throw error;
    }
  },

  updateFeatured: async (id, updates) => {
    try {
      const response = await api.put(`/admin/featured/${id}`, updates);
      return response;
    } catch (error) {
      throw error;
    }
  },

  deleteFeatured: async (id) => {
    try {
      const response = await api.delete(`/admin/featured/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  reorderFeatured: async (items) => {
    try {
      const response = await api.put('/admin/featured/reorder', { items });
      return response;
    } catch (error) {
      throw error;
    }
  },

  searchContent: async (query, type = 'video') => {
    try {
      const response = await api.get('/admin/featured/search', { params: { query, type } });
      return response;
    } catch (error) {
      throw error;
    }
  }
};

// Test connection function (from your provided file)
export const testConnection = async () => { //
  try {
    const response = await api.get('/health');
    return true;
  } catch (error) {
    return false;
  }
};

// Enhanced error handling utility (from your provided file)
export const handleAPIError = (error, fallbackMessage = 'An error occurred') => { //
  if (error.response) {
    const { status, data } = error.response;
    if (data?.error) return data.error;
    if (data?.message) return data.message;
    return `Server error: ${status}`;
  } else if (error.request) {
    return 'No response from server. Please check your connection.';
  } else {
    return error.message || fallbackMessage;
  }
};

// Utility for building query parameters (from your provided file)
export const buildQueryParams = (params) => { //
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

// ========================================
// AUTH API
// ========================================

export const authAPI = {
  // Register new account
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Login
  login: async (identifier, password) => {
    try {
      const response = await api.post('/auth/login', { identifier, password });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Logout
  logout: async () => {
    try {
      const response = await api.post('/auth/logout');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Admin: Get all accounts
  getAccounts: async (options = {}) => {
    try {
      const { search, role, page = 1, limit = 50 } = options;
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (role) params.append('role', role);
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const response = await api.get(`/auth/admin/accounts?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Admin: Impersonate account
  impersonate: async (targetUserId) => {
    try {
      const response = await api.post('/auth/admin/impersonate', { targetUserId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Save first-login consent preferences
  saveMarketingConsent: async (consent, privacyPolicyAccepted, privacyPolicyVersion = '1.0') => {
    try {
      const response = await api.post('/auth/marketing-consent', {
        consent,
        privacyPolicyAccepted,
        privacyPolicyVersion
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createTestAccount: async (accountData) => {
    try {
      const response = await api.post('/auth/admin/create-test-account', accountData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ========================================
// FORUM API
// ========================================

export const forumAPI = {
  // Get thread preview for content (peak moments, movies, etc.)
  getThreadPreview: async (contentType, contentId) => {
    try {
      const response = await api.get(`/forum/preview/${contentType}/${contentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all threads with filters
  getThreads: async (options = {}) => {
    try {
      const {
        category,
        contentType,
        contentId,
        page = 1,
        limit = 20,
        sort = 'latest'
      } = options;

      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (contentType) params.append('contentType', contentType);
      if (contentId) params.append('contentId', contentId);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('sort', sort);

      const response = await api.get(`/forum/threads?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get single thread by slug
  getThread: async (slug) => {
    try {
      const response = await api.get(`/forum/threads/${slug}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new thread
  createThread: async (threadData) => {
    try {
      const response = await api.post('/forum/threads', threadData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update thread
  updateThread: async (id, updates) => {
    try {
      const response = await api.put(`/forum/threads/${id}`, updates);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete thread
  deleteThread: async (id) => {
    try {
      const response = await api.delete(`/forum/threads/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Like/unlike thread
  likeThread: async (id) => {
    try {
      const response = await api.post(`/forum/threads/${id}/like`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get posts for a thread
  getPosts: async (threadId, options = {}) => {
    try {
      const { page = 1, limit = 20, sortBy = 'createdAt', order = 'asc' } = options;

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('sortBy', sortBy);
      params.append('order', order);

      const response = await api.get(`/forum/threads/${threadId}/posts?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create post/reply
  createPost: async (threadId, postData) => {
    try {
      const response = await api.post(`/forum/threads/${threadId}/posts`, postData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update post
  updatePost: async (postId, updates) => {
    try {
      const response = await api.put(`/forum/posts/${postId}`, updates);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete post
  deletePost: async (postId) => {
    try {
      const response = await api.delete(`/forum/posts/${postId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Like/unlike post
  likePost: async (postId) => {
    try {
      const response = await api.post(`/forum/posts/${postId}/like`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Search threads
  searchThreads: async (query, options = {}) => {
    try {
      const { category, contentType, tags, page = 1, limit = 20 } = options;

      const params = new URLSearchParams();
      params.append('q', query);
      if (category) params.append('category', category);
      if (contentType) params.append('contentType', contentType);
      if (tags) params.append('tags', tags);
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const response = await api.get(`/forum/search?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get trending threads
  getTrending: async (limit = 20) => {
    try {
      const response = await api.get('/forum/trending', { params: { limit } });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// AI Assistant API
export const aiAssistantAPI = {
  // Send a chat message to the AI
  chat: async (message, sessionId = 'default') => {
    try {
      const response = await api.post('/admin/ai/chat', { message, sessionId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Clear conversation history
  clearHistory: async (sessionId = 'default') => {
    try {
      const response = await api.post('/admin/ai/clear', { sessionId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get current context/stats
  getContext: async () => {
    try {
      const response = await api.get('/admin/ai/context');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Search channels via AI
  searchChannels: async (query, limit = 10) => {
    try {
      const response = await api.get('/admin/ai/actions/search-channels', { params: { query, limit } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Search media via AI
  searchMedia: async (query, type, limit = 10) => {
    try {
      const response = await api.get('/admin/ai/actions/search-media', { params: { query, type, limit } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get pending counts
  getPendingCounts: async () => {
    try {
      const response = await api.get('/admin/ai/actions/pending-count');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get forum activity
  getForumActivity: async (limit = 10) => {
    try {
      const response = await api.get('/admin/ai/actions/forum-activity', { params: { limit } });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Creator API
export const creatorAPI = {
  getMe: async () => {
    const response = await api.get('/creator/me');
    return response.data;
  },
  validateChannel: async (channelUrl) => {
    const response = await api.post('/creator/claim/validate-channel', { channelUrl });
    return response.data;
  },
  initiateClaim: async (channelUrl, consentId) => {
    const response = await api.post('/creator/claim/initiate', { channelUrl, consentId });
    return response.data;
  },
  verifyClaim: async (method, videoId) => {
    const response = await api.post('/creator/claim/verify', { method, videoId });
    return response.data;
  },
  getClaimStatus: async () => {
    const response = await api.get('/creator/claim/status');
    return response.data;
  },
  revokeClaim: async () => {
    const response = await api.delete('/creator/claim');
    return response.data;
  },
  updateProfile: async (data) => {
    const response = await api.put('/creator/profile', data);
    return response.data;
  },
  getDashboard: async () => {
    const response = await api.get('/creator/dashboard');
    return response.data;
  },
  getClicks: async (period = '30d') => {
    const response = await api.get('/creator/clicks', { params: { period } });
    return response.data;
  },
  getPeakMoments: async () => {
    const response = await api.get('/creator/peak-moments');
    return response.data;
  },
  setOfficialPicks: async (pickIds) => {
    const response = await api.put('/creator/official-picks', { pickIds });
    return response.data;
  },
  submitReport: async (data) => {
    const response = await api.post('/creator/report', data);
    return response.data;
  },
  getReports: async () => {
    const response = await api.get('/creator/reports');
    return response.data;
  },
  getPublicProfile: async (channelId) => {
    const response = await api.get(`/creator/profile/${channelId}`);
    return response.data;
  },
  submitConsent: async (data) => {
    const response = await api.post('/creator/consent', data);
    return response.data;
  },
  getConsentText: async () => {
    const response = await api.get('/creator/consent/text');
    return response.data;
  },
  requestExclusion: async (data) => {
    const response = await api.post('/creator/request-exclusion', data);
    return response.data;
  },
  trackClick: (channelId, source, videoId, sessionId, clickType, mediaCategory, contentId, contentTitle, contentType) => {
    const _u = getUserFromToken();
    api.post('/creator/track-click', { channelId, source, videoId, sessionId, clickType, mediaCategory, contentId, contentTitle, contentType, userEmail: _u?.email || null }).catch(() => {});
  },
  trackBoostImpression: (channelId) => {
    api.post('/creator/boost/impression', { channelId }).catch(() => {});
  },
  getBoostStatus: async () => {
    const response = await api.get('/creator/boost');
    return response.data;
  },
  createCheckoutSession: async (data) => {
    const response = await api.post('/creator/boost/checkout', data);
    return response.data;
  },
  updateBoostCarousel: async (carouselVideos) => {
    const response = await api.put('/creator/boost/carousel', { carouselVideos });
    return response.data;
  },
};

export default api;
